import React, { createContext, useCallback, useContext, useMemo, useState } from "react"

/**
 * Avatar display modes
 */
export type AvatarMode = "large" | "minimized" | "tab"

/**
 * Avatar snap corner positions (for minimized mode)
 */
export type AvatarSnapCorner = "top-left" | "top-right" | "bottom-left" | "bottom-right"

interface AvatarModeContextType {
  mode: AvatarMode
  setMode: (mode: AvatarMode, isManual?: boolean) => void
  snapCorner: AvatarSnapCorner
  setSnapCorner: (corner: AvatarSnapCorner) => void
  isMuted: boolean
  setIsMuted: (muted: boolean) => void
  captionsEnabled: boolean
  setCaptionsEnabled: (enabled: boolean) => void
  rememberedSize: AvatarMode
  getRememberedSize: () => AvatarMode
}

const AvatarModeContext = createContext<AvatarModeContextType | undefined>(undefined)

/**
 * Provider component that persists avatar mode, snap corner, mute state, and captions state across screens
 * Also tracks "remembered size" for automatic vs manual transitions
 */
export function AvatarModeProvider({ children }: { children: React.ReactNode }) {
  const [mode, setModeState] = useState<AvatarMode>("minimized")
  const [rememberedSize, setRememberedSize] = useState<AvatarMode>("minimized")
  const [snapCorner, setSnapCornerState] = useState<AvatarSnapCorner>("top-left")
  const [isMuted, setIsMutedState] = useState(false)
  const [captionsEnabled, setCaptionsEnabledState] = useState(true)

  // Keep a ref so setMode can log the current value without capturing it as a dep.
  // Previously rememberedSize was a dep, which caused setMode to be recreated on every
  // manual transition, propagating unnecessary re-renders to all consumers.
  const rememberedSizeRef = React.useRef(rememberedSize)
  rememberedSizeRef.current = rememberedSize

  const setMode = useCallback(
    (newMode: AvatarMode, isManual = false) => {
      if (__DEV__) {
        console.warn(
          `[AvatarModeContext] setMode: ${newMode}, isManual: ${isManual}, current remembered: ${rememberedSizeRef.current}`,
        )
      }
      setModeState(newMode)
      // Only update remembered size for manual transitions
      if (isManual) {
        if (__DEV__) {
          console.warn(`[AvatarModeContext] Updating remembered size to: ${newMode}`)
        }
        setRememberedSize(newMode)
      }
    },
    [], // setModeState and setRememberedSize are stable React setState functions
  )

  const getRememberedSize = useCallback(() => {
    return rememberedSize
  }, [rememberedSize])

  const setSnapCorner = useCallback((corner: AvatarSnapCorner) => {
    setSnapCornerState(corner)
  }, [])

  const setIsMuted = useCallback((muted: boolean) => {
    setIsMutedState(muted)
  }, [])

  const setCaptionsEnabled = useCallback((enabled: boolean) => {
    setCaptionsEnabledState(enabled)
  }, [])

  const value = useMemo(
    () => ({
      mode,
      setMode,
      snapCorner,
      setSnapCorner,
      isMuted,
      setIsMuted,
      captionsEnabled,
      setCaptionsEnabled,
      rememberedSize,
      getRememberedSize,
    }),
    [
      mode,
      setMode,
      snapCorner,
      setSnapCorner,
      isMuted,
      setIsMuted,
      captionsEnabled,
      setCaptionsEnabled,
      rememberedSize,
      getRememberedSize,
    ],
  )

  return <AvatarModeContext.Provider value={value}>{children}</AvatarModeContext.Provider>
}

/**
 * Hook to access and update the avatar mode
 */
export function useAvatarMode() {
  const context = useContext(AvatarModeContext)
  if (context === undefined) {
    throw new Error("useAvatarMode must be used within an AvatarModeProvider")
  }
  return context
}

/**
 * Optional hook that doesn't throw if context is missing
 */
export function useOptionalAvatarMode() {
  return useContext(AvatarModeContext)
}
